import { Box, Button, Card, CardContent, Divider, Grid, InputAdornment, Typography } from "@mui/material";
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { apiConstants, getGeneric, post } from "../../../common/constants";
import { FormProvider, RHFCheckbox2, RHFTextField } from "../../../common/hook-form";
import { MerchantFilterCached } from "../../../common/ProshipFormComponents";
import { useSnackbar } from "notistack";
import Loader from "../../../common/Loader";
import { FieldLabel } from "../../../common/FilterBar";
import { LoadingButton } from "@mui/lab";
import * as Yup from 'yup';
import { yupResolver } from "@hookform/resolvers/yup";
import { formatDate } from "../../../common/util";

// ─── Info row for upcoming-changes panel ─────────────────────────────────────

function InfoRow({ label, value }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
            <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1a1a2e' }}>
                {value ?? '—'}
            </Typography>
        </Box>
    );
}

// ─── Component ────────────────────────────────────────────────────────────────

function CODConfig() {
    const [latestCODCycle, setLatestCODCycle] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { enqueueSnackbar } = useSnackbar();

    const methods = useForm({
        resolver: yupResolver(
            Yup.object().shape({
                merchant: Yup.string().required("Please select Merchant"),
                postDeliverySettlementDays: Yup.number()
                    .transform((value, originalValue) => (originalValue === '' || originalValue === null || originalValue === undefined ? 0 : value))
                    .min(0, "Post Delivery Settlement Days must be 0 or greater")
                    .default(0),
            })
        ),
        defaultValues: {
            merchant: '',
            earlyCODEnabled: false,
            commissionPercent: '',
            noOfDays: '',
            postDeliverySettlementDays: 0,
        }
    });

    const { handleSubmit, reset, watch, setValue } = methods;
    const isEarlyCod = watch('earlyCODEnabled');
    const merchantN = watch('merchant');
    const cycleDays = watch('noOfDays');

    useEffect(() => {
        if (!merchantN) return;
        setIsLoading(true);
        getGeneric(`${apiConstants.COD_CONFIG}?merchantId=${merchantN}`)
            .then((res) => {
                setIsLoading(false);
                const obj = res.data.responseObj;
                if (obj === null) {
                    setValue("earlyCODEnabled", '');
                    setValue("commissionPercent", '');
                    setValue("noOfDays", '');
                    setValue("postDeliverySettlementDays", 0);
                    setLatestCODCycle(null);
                } else {
                    setValue("earlyCODEnabled", obj.earlyCODEnabled);
                    setValue("commissionPercent", obj.earlyCODComissionPercent);
                    setValue("noOfDays", obj.cycleOfDays);
                    setValue("postDeliverySettlementDays", obj.postDeliverySettlementDays ?? 0);
                    setLatestCODCycle(obj.latestCODCycle || null);
                }
            })
            .catch(err => { setIsLoading(false); console.error(err); });
    }, [merchantN]); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = (data) => {
        setIsSubmitting(true);
        post(apiConstants.COD_CONFIG_UPDATE, {
            merchantId: data.merchant,
            codRemittanceEnabled: true,
            noOfDays: data.noOfDays,
            earlyCODEnabled: data.earlyCODEnabled,
            commissionPercent: data.commissionPercent,
            postDeliverySettlementDays: data.postDeliverySettlementDays ?? 0,
        })
            .then((res) => {
                if (res.status === 200 && res.data.status === "SUCCESS") {
                    enqueueSnackbar('COD Config updated successfully', { variant: 'success' });
                    reset();
                    setLatestCODCycle(null);
                } else {
                    enqueueSnackbar(res?.data?.errorMsg || 'Some error occurred', { variant: 'error' });
                }
            })
            .catch((err) => console.log(err))
            .finally(() => setIsSubmitting(false));
    };

    return (
        <>
            {isLoading && <Loader />}

            {/* ── Config form ───────────────────────────────────────────── */}
            <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #e8e8e8', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', mb: 2 }}>
                <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#1a1a2e' }}>COD Configuration</Typography>
                    <Typography sx={{ fontSize: '12px', color: '#9ca3af', mt: '2px' }}>
                        Set COD cycle, settlement days and early COD settings for a merchant
                    </Typography>
                </Box>
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                        <Grid container spacing={2} alignItems="flex-end">

                            <Grid item xs={12} md={4}>
                                <FieldLabel>Merchant *</FieldLabel>
                                <MerchantFilterCached fullWidth />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FieldLabel>COD Cycle</FieldLabel>
                                <RHFTextField
                                    name="noOfDays"
                                    label=""
                                    size="small"
                                    fullWidth
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ endAdornment: <InputAdornment position="end"><Box sx={{ fontSize: '11px', color: '#9ca3af' }}>days</Box></InputAdornment> }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FieldLabel>Post Delivery Settlement Days</FieldLabel>
                                <RHFTextField
                                    name="postDeliverySettlementDays"
                                    label=""
                                    size="small"
                                    fullWidth
                                    type="number"
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{
                                        endAdornment: <InputAdornment position="end"><Box sx={{ fontSize: '11px', color: '#9ca3af' }}>days</Box></InputAdornment>,
                                        inputProps: { min: 0 },
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FieldLabel>Early COD</FieldLabel>
                                <Box sx={{
                                    border: '1px solid #e0e0e0', borderRadius: '8px',
                                    height: 40, display: 'flex', alignItems: 'center', px: 1.5,
                                    bgcolor: !cycleDays ? '#f9fafb' : '#fff',
                                    '&:hover': { borderColor: cycleDays ? '#9ca3af' : '#e0e0e0' },
                                }}>
                                    <RHFCheckbox2 name="earlyCODEnabled" label="Enable Early COD" disabled={!cycleDays} />
                                </Box>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <FieldLabel>Commission Percent</FieldLabel>
                                <RHFTextField
                                    name="commissionPercent"
                                    label=""
                                    size="small"
                                    fullWidth
                                    disabled={!isEarlyCod}
                                    InputLabelProps={{ shrink: true }}
                                    InputProps={{ endAdornment: <InputAdornment position="end"><Box sx={{ fontSize: '11px', color: '#9ca3af' }}>%</Box></InputAdornment> }}
                                />
                            </Grid>

                            <Grid item xs={12} md={4} />

                            <Grid item xs={12}>
                                <Divider sx={{ mb: 2, borderColor: '#f0f0f0' }} />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <LoadingButton
                                        loading={isSubmitting}
                                        type="submit"
                                        variant="contained"
                                        size="small"
                                        disableElevation
                                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2.5, py: 1, bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' } }}
                                    >
                                        Save Config
                                    </LoadingButton>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        color="error"
                                        onClick={() => reset()}
                                        sx={{ fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', px: 2, py: 0.875 }}
                                    >
                                        Reset
                                    </Button>
                                </Box>
                            </Grid>

                        </Grid>
                    </FormProvider>
                </CardContent>
            </Card>

            {/* ── Upcoming changes panel ────────────────────────────────── */}
            {latestCODCycle && (
                <Card elevation={0} sx={{ borderRadius: '10px', border: '1px solid #fde68a', boxShadow: '0 1px 3px rgba(0,0,0,0.06)', bgcolor: '#fffbeb' }}>
                    <Box sx={{ px: 2.5, py: 1.75, borderBottom: '1px solid #fde68a', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScheduleOutlinedIcon sx={{ fontSize: 18, color: '#b45309' }} />
                        <Box>
                            <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#92400e' }}>
                                Upcoming Changes to COD Configuration
                            </Typography>
                            <Typography sx={{ fontSize: '11.5px', color: '#b45309', mt: '1px' }}>
                                {new Date(latestCODCycle.cycleStartDate) < new Date()
                                    ? 'Changes will be applied in the next 4 hours'
                                    : `Changes will be applied on ${formatDate(new Date(latestCODCycle.cycleStartDate), { format: 'dd MMM, yyyy' })}`}
                            </Typography>
                        </Box>
                    </Box>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                        <Grid container spacing={3}>
                            <Grid item xs={6} md={3}>
                                <InfoRow label="COD Cycle" value={latestCODCycle.noOfDays != null ? `${latestCODCycle.noOfDays} days` : null} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <InfoRow label="Post Delivery Settlement" value={latestCODCycle.postDeliverySettlementDays != null ? `${latestCODCycle.postDeliverySettlementDays} days` : '0 days'} />
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                                    <Typography sx={{ fontSize: '10px', fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                                        Early COD
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        {latestCODCycle.earlyCODEnabled
                                            ? <CheckCircleOutlineIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                                            : <CancelOutlinedIcon sx={{ fontSize: 16, color: '#dc2626' }} />}
                                        <Typography sx={{ fontSize: '14px', fontWeight: 600, color: latestCODCycle.earlyCODEnabled ? '#16a34a' : '#dc2626' }}>
                                            {latestCODCycle.earlyCODEnabled ? 'Enabled' : 'Disabled'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid item xs={6} md={3}>
                                <InfoRow label="Commission Percent" value={latestCODCycle.earlyCODComissionPercent != null ? `${latestCODCycle.earlyCODComissionPercent}%` : null} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            )}
        </>
    );
}

export default CODConfig;
